import { INTEGRATION_KEYS } from './integrationGate';
import { isTenantId } from './teamsOAuth';

// Teams PR A: Admin Center > Integrations > Microsoft Teams. The defaults below are the database defaults (migration 235):
// everything off or narrowest until a Tenant Admin changes it. The database re-checks every value and every permission.

export const TEAMS_KEY = INTEGRATION_KEYS.MICROSOFT_TEAMS;
export const ALERT_OPTIONS = Object.freeze([['mentions', 'Mentions only'], ['every', 'Every message'], ['off', 'Off']]);

export const DEFAULT_INTEGRATION = Object.freeze({
    enabled: false,
    assignment_mode: 'people',
    assigned_roles: [],
    assigned_user_ids: [],
});

export const DEFAULT_TEAMS_SETTINGS = Object.freeze({
    linked_m365_tenant_id: '',
    linked_m365_tenant_name: '',
    allow_personal_chats: false,
    allow_channels: false,
    allow_shared_channels: false,
    allow_onedrive_attach: false,
    allow_sharepoint_links: false,
    allow_sharepoint_storage: false,
    reply_mode: 'view_only',
    directory_scope: 'my_teams',
    directory_group_ids: [],
    outside_people: 'off',
    approved_domains: [],
    require_crm_contact: true,
    allow_outside_attachments: false,
    allowed_sharepoint_sites: [],
    disconnect_rule: 'keep_read_only',
});

export const USE_SWITCHES = Object.freeze([
    { key: 'allow_personal_chats', label: 'Personal chats' },
    { key: 'allow_channels', label: 'Channels' },
    { key: 'allow_shared_channels', label: 'Teams shared channels (span two companies)' },
    { key: 'allow_onedrive_attach', label: 'OneDrive: attach files' },
    { key: 'allow_sharepoint_links', label: 'SharePoint: file links and previews' },
    { key: 'allow_sharepoint_storage', label: 'SharePoint as Documents storage' },
]);

export const REPLY_OPTIONS = Object.freeze([['two_way', 'Two-way'], ['view_only', 'View only']]);
// "All staff" is the first option, labelled exactly so (directive A1).
export const WHO_OPTIONS = Object.freeze([['all_staff', 'All staff'], ['roles', 'Chosen roles'], ['people', 'Named people']]);
export const DIRECTORY_OPTIONS = Object.freeze([['whole_org', 'Whole organization'], ['my_teams', 'Only their IntelAgent teams'], ['groups', 'Chosen groups']]);
export const OUTSIDE_OPTIONS = Object.freeze([['off', 'Off'], ['approved_domains', 'Approved domains only'], ['everything', 'Everything Microsoft allows']]);
export const DISCONNECT_OPTIONS = Object.freeze([['keep_read_only', 'Keep history read-only'], ['purge', 'Purge their mirror']]);

const DOMAIN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** '@Client.COM ' -> 'client.com'; anything that is not a domain -> null. */
export function normalizeDomain(value) {
    const domain = String(value || '').trim().replace(/^@+/, '').toLowerCase();
    return DOMAIN.test(domain) ? domain : null;
}

export function isSharePointUrl(value) {
    return /^https:\/\/[a-z0-9.-]+\.sharepoint\.com\//i.test(String(value || '').trim());
}

/** The first problem with a draft, in plain English, or '' when it can be saved. */
export function settingsProblem(settings, integration) {
    if (settings.linked_m365_tenant_id && !isTenantId(settings.linked_m365_tenant_id)) {
        return 'The Microsoft 365 organization id must look like 00000000-0000-0000-0000-000000000000.';
    }
    if (integration?.enabled && !settings.linked_m365_tenant_id) {
        return 'Link your Microsoft 365 organization before turning Teams on.';
    }
    if (settings.outside_people === 'approved_domains' && settings.approved_domains.length === 0) {
        return 'Add at least one approved domain, or choose Off.';
    }
    if (settings.directory_scope === 'groups' && settings.directory_group_ids.length === 0) {
        return 'Pick at least one group for the people directory.';
    }
    if (integration?.assignment_mode === 'roles' && integration.assigned_roles.length === 0 && integration.enabled) {
        return 'Choose at least one role, or pick All staff or Named people.';
    }
    if (integration?.assignment_mode === 'people' && integration.assigned_user_ids.length === 0 && integration.enabled) {
        return 'Name at least one person, or pick All staff or Chosen roles.';
    }
    return '';
}

function pick(source, defaults) {
    const out = {};
    Object.keys(defaults).forEach((key) => {
        const value = source && source[key] !== undefined && source[key] !== null ? source[key] : defaults[key];
        out[key] = Array.isArray(defaults[key]) ? [...(value || [])] : value;
    });
    return out;
}

export function settingsFromRow(row) { return pick(row, DEFAULT_TEAMS_SETTINGS); }
export function integrationFromRow(row) { return pick(row, DEFAULT_INTEGRATION); }

/** Everything the Teams admin page shows. */
export async function loadTeamsAdmin(client, orgId) {
    const [integration, settings, teams, people, presets, audit] = await Promise.all([
        client.from('org_integrations').select('*').eq('org_id', orgId).eq('integration_key', TEAMS_KEY).maybeSingle(),
        client.from('org_teams_settings').select('*').eq('org_id', orgId).maybeSingle(),
        client.rpc('team_spaces_list'),
        client.rpc('team_candidate_people'),
        client.from('org_role_presets').select('key, name').eq('org_id', orgId).order('name'),
        client.from('integration_audit_log').select('id', { count: 'exact', head: true }).eq('integration_key', TEAMS_KEY),
    ]);
    const failure = [integration, settings, teams, people].find((result) => result.error);
    if (failure) throw failure.error;
    return {
        integration: integrationFromRow(integration.data),
        settings: settingsFromRow(settings.data),
        teams: teams.data || [],
        people: people.data || [],
        roles: [{ key: 'owner', name: 'Owner' }, ...((presets.data || []).filter((preset) => preset.key !== 'owner'))],
        auditCount: audit.count || 0,
    };
}

/** One Save: the switch row, then the settings row. Each write is audit-logged by the database. */
export async function saveTeamsAdmin(client, orgId, integration, settings) {
    const settingsRow = { org_id: orgId, ...settings, linked_m365_tenant_id: settings.linked_m365_tenant_id || null };
    const savedSettings = await client.from('org_teams_settings').upsert(settingsRow, { onConflict: 'org_id' }).select().single();
    if (savedSettings.error) throw savedSettings.error;
    const savedIntegration = await client.from('org_integrations')
        .upsert({ org_id: orgId, integration_key: TEAMS_KEY, ...integration }, { onConflict: 'org_id,integration_key' })
        .select().single();
    if (savedIntegration.error) throw savedIntegration.error;
    return { integration: integrationFromRow(savedIntegration.data), settings: settingsFromRow(savedSettings.data) };
}

export async function assignTeamOwner(client, teamId, userId) {
    const { error } = userId
        ? await client.rpc('team_assign_owner', { p_team: teamId, p_user: userId })
        : await client.rpc('team_clear_owner', { p_team: teamId });
    if (error) throw error;
}

export async function loadAuditLog(client, limit = 50) {
    const { data, error } = await client.from('integration_audit_log')
        .select('id, created_at, actor_id, action, target_table, target_id, before, after')
        .eq('integration_key', TEAMS_KEY).order('created_at', { ascending: false }).limit(limit);
    if (error) throw error;
    return data || [];
}

const TABLE_WORDS = {
    org_integrations: 'Switch and who gets Teams',
    org_teams_settings: 'Teams settings',
    org_team_members: 'Team membership',
    org_team_settings: 'Team settings',
    teams_connections: 'Personal connection',
    tenant_entitlements: 'Onboarding choice',
};

/** One plain-English line per audit row. */
export function describeAuditRow(row) {
    const what = TABLE_WORDS[row.target_table] || row.target_table;
    const verb = { insert: 'created', update: 'changed', delete: 'removed', opt_in: 'opted in' }[row.action] || row.action;
    const changed = row.before && row.after
        ? Object.keys(row.after).filter((key) => JSON.stringify(row.after[key]) !== JSON.stringify(row.before[key]))
        : [];
    return `${what} ${verb}${changed.length ? `: ${changed.join(', ')}` : ''}`;
}
